import { inject, Injectable } from '@angular/core'
import { Assistance } from '../../../models/assistance'
import { FeathersService } from '../../feathers.service'
import { filter, map, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators'
import { firstValueFrom, Observable } from 'rxjs'
import { AssistanceHooks } from '../../../feathersHooks/assistance.hooks'
import { RecurrenceService } from '../recurrence/recurrence.service'
import { RecurrenceEditingMode } from '../../../features/recurrence/recurrenceEditing'
import { AssistantAssignment } from '../../../models/assistantAssignment'
import { AssistantAvailability } from '../../../models/assistantAvailability'
import { AssistanceStatusName } from '../../../models/assistanceStatus'
import { AssistanceStatusManagerService } from '../../assistance-status-manager.service'
import { DateRange, DateUtils } from '../../date-utils.service'
import { coerceToArray } from '../../utils'
import { ListAssistanceLockService } from '../../../features/assistance/assistance-list-new/list-assistance-lock.service'
import { addMinutes, subDays, subMinutes } from 'date-fns'
import { AlertController } from '@ionic/angular'
import { RecurrenceUtil } from '../../../features/recurrence/RecurrenceUtil'
import { WorkgroupService } from '../workgroup/workgroup.service'
import { PartialWithId } from '../../../shared/utils/typeUtils'
import {
  AssignmentsManagerService,
  RemovalResult,
  SkippedAssignment,
} from '../../../features/assignments/assignments-manager.service'
import { CalendarAssistantAvailabilityAssistance } from '../../../models/CalendarAssistantAvailabilityAssistance'
import { PrimeNgToastService } from '../../prime-ng-toast/prime-ng-toast.service'

interface AssistanceChangesCount {
  id: number
  count: number
}

export type AssistanceQueryType =
  | 'calendar'
  | 'list'
  | 'full'
  | 'roleAssistantCalendar'
  | 'roleAssistantExchangeCalendar'
  | 'noAssociations'
  | 'assistanceMessagingList'
  | 'financialReports'

@Injectable({
  providedIn: 'root',
})
export class AssistanceService extends RecurrenceService {
  private feathers = inject(FeathersService)
  toaster: PrimeNgToastService
  private statusManager = inject(AssistanceStatusManagerService)
  private assistanceLocker = inject(ListAssistanceLockService)
  private alertCtrl = inject(AlertController)
  private assignmentService = inject(AssignmentsManagerService)
  private primeNgToastService = inject(PrimeNgToastService)

  TAG: string = 'AssistanceService'

  allStream$: Observable<Assistance[]>

  constructor() {
    const toaster = inject(PrimeNgToastService)

    super(toaster)
    this.toaster = toaster

    console.log(this.TAG, 'constructor')
    // this.init()
    // this.stream$.subscribe()
  }

  private init() {
    console.log(this.TAG, 'init')
    this.allStream$ = this.feathers
      .find(Assistance, {})
      .pipe(
        map((assistances: Assistance[]) =>
          assistances.map((a) => AssistanceHooks.convertAssignmentToAssistantAssignmentList(a)),
        ),
      )
      .pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  getAssistancesByAssistantId(assistantId: number, query: any = {}): Observable<Assistance[]> {
    console.log('getAssistanceByAssistant', assistantId)
    query.assistantId = assistantId

    return this.feathers.find(AssistantAssignment, query).pipe(
      map((assignments) => assignments.map((assignment: AssistantAssignment) => assignment.assistanceId)),
      mergeMap((assistanceIds) =>
        this.queryFeathers({
          id: { $in: assistanceIds },
        }),
      ),
    )
  }

  getAssistancesCountByAssistantId(assistantId: number): Promise<number> {
    console.log('getAssistancesCountByAssistantId', assistantId)

    return this.feathers.count(AssistantAssignment.serviceName, {
      queryType: 'roleAssistantMyAssistancesCountInBadge',
      assistantId,
    })
  }

  getAllWithCaching(): Observable<Assistance[]> {
    // debugger
    if (!this.allStream$) {
      this.init()
    }
    console.log(this.TAG, 'getAllWithCaching', this.allStream$)
    return this.allStream$
  }

  getByPlannedDateRangeByWorkgroupsAndStatuses(
    range: DateRange,
    statusNames: AssistanceStatusName[],
    queryType: AssistanceQueryType = 'full',
    workgroupIds?: (number | string)[],
    additionalQuery = {},
  ): Observable<Assistance[]> {
    if (workgroupIds && workgroupIds.includes(WorkgroupService.ALL_WORKGROUPS)) {
      workgroupIds = undefined
    }

    return this.statusManager
      .getStatuses()
      .pipe(
        map((statuses) => {
          const statusIds: number[] = []
          for (const s of statuses) {
            if (statusNames.find((name) => name === s.name)) {
              statusIds.push(s.id)
            }
          }

          const query: any = {
            ...additionalQuery,
            queryType,
            workgroupIds,
            statusId: { $in: coerceToArray(statusIds) },
            $or: [
              {
                plannedFrom: {
                  $lte: range.to,
                  $gte: range.from,
                },
                rrule: null,
              },

              {
                plannedFrom: {
                  $lte: range.to,
                  $gte: DateUtils.subtractDaysFromDate(range.from, 365),
                },
                recurrenceEnd: {
                  $or: [{ $gte: subDays(range.from, 1) }, null],
                },
                rrule: { $ne: null },
              },
            ],
            $limit: 2500,
          }

          return query
        }),
      )
      .pipe(switchMap((query) => this.queryFeathers(query)))
  }

  getByPlannedDateRangeAndClient(range: DateRange, clientId: number) {
    const query: any = {
      clientId,
      $or: [
        {
          plannedFrom: {
            $lte: range.to,
            $gte: range.from,
          },
          rrule: null,
        },

        {
          plannedFrom: {
            $lte: range.to,
            $gte: DateUtils.subtractDaysFromDate(range.from, 365),
          },
          recurrenceEnd: {
            $or: [{ $gte: subDays(range.from, 1) }, null],
          },
          rrule: { $ne: null },
        },
      ],
      // $limit: 3000
    }

    return this.queryFeathers(query)
  }

  async getAssistancesAroundRangeForAssistants(range: DateRange, minutesAround: number, assistantIds: number[]) {
    const exceptStatuses = await firstValueFrom(
      this.statusManager.getStatusesByNamesAsync([AssistanceStatusName.CANCELED, AssistanceStatusName.CANCELED_LATE]),
    )
    const query: any = {
      queryType: 'assistantSelectAssistancesAround',
      assistantIds,
      statusId: {
        $nin: exceptStatuses.map((status) => status.id),
      },
      $or: [
        {
          plannedTo: {
            $gte: subMinutes(range.from, minutesAround),
            $lte: range.from,
          },
        },
        {
          plannedFrom: {
            $gte: range.to,
            $lte: addMinutes(range.to, minutesAround),
          },
        },
      ],
      rrule: null,
    }
    return this.queryFeathers(query)
  }

  /**
   * Gets all Assistances that fall into a given date range, recurring and non-recurring.
   *
   * Query explanation:
   * There can be either a one-off Assistance or a recurring one.
   * With a recurring assistance, we cannot select an ocurrence directly from the database as they are zipped up inside an Rrule.
   * So in case of a recurring assistance, we have to find the parent record, ie. the one that is first in the recurrence series.
   *
   * If the parent Assistance falls inside the given date range, it will be selected the same way as the one-off Assistance.
   * If the parent Assistance only has occurrences (not the parent record itself) inside the given range, its parent record will start earlier than the range start, it will have an rrule field set and it may or may not have a recurrenceEnd field set (infinite vs. finite recurrence).
   *
   * The result is the most precise we can get from the DB and is intended to have its RRULEs expanded client-side and further filtered
   *
   */
  getByPlannedDateRange(range: DateRange, queryType: AssistanceQueryType = 'full'): Observable<Assistance[]> {
    const query: any = {
      queryType,
      $or: [
        {
          plannedFrom: {
            $lte: range.to,
            $gte: range.from,
          },
          rrule: null,
        },

        {
          plannedFrom: {
            $lte: range.to,
            $gte: DateUtils.subtractDaysFromDate(range.from, 365),
          },
          recurrenceEnd: {
            $or: [{ $gte: subDays(range.from, 1) }, null],
          },
          rrule: { $ne: null },
        },
      ],
    }

    return this.queryFeathers(query)
  }

  getByPlannedDateRangeRecurrentAndNonRecurrent(
    range: DateRange,
    queryType: AssistanceQueryType = 'full',
  ): Observable<any[]> {
    const query: any = {
      queryType,
      $or: [
        {
          plannedFrom: {
            $lte: range.to,
            $gte: range.from,
          },
          rrule: null,
        },
        {
          plannedFrom: {
            $lte: range.to,
            $gte: DateUtils.subtractDaysFromDate(range.from, 365),
          },
          recurrenceEnd: {
            $or: [{ $gte: subDays(range.from, 1) }, null],
          },
          rrule: { $ne: null },
        },
      ],
    }

    return this.queryFeathers(query)
    // return this.feathers.getObservable('assistances', query).pipe(tap(e => console.log('experiment', e)))
  }

  getCalendarAssistantAvailabilities(range: DateRange): Observable<CalendarAssistantAvailabilityAssistance[]> {
    return this.feathers.find(CalendarAssistantAvailabilityAssistance, {
      dateRangeFrom: range.from.toISOString(),
      dateRangeTo: range.to.toISOString(),
    })
  }

  getByPlannedDateRangeNonRecurrent(
    range: DateRange,
    queryType: AssistanceQueryType = 'full',
  ): Observable<Assistance[]> {
    const query: any = {
      queryType,
      plannedFrom: {
        $lte: range.to,
        $gte: range.from,
      },
      rrule: null,
      // $limit: 3000
    }
    return this.queryFeathers(query)
  }

  getByPlannedDateRangeRecurrent(range: DateRange, queryType: AssistanceQueryType = 'full'): Observable<Assistance[]> {
    const query: any = {
      queryType,
      plannedFrom: {
        $lte: range.to,
        $gte: DateUtils.subtractDaysFromDate(range.from, 365),
      },
      recurrenceEnd: {
        $or: [{ $gte: subDays(range.from, 1) }, null],
      },
      rrule: { $ne: null },
      // $limit: 3000
    }

    return this.queryFeathers(query)
  }

  queryFeathers(query: {}): Observable<Assistance[]> {
    return this.feathers.find(Assistance, query)
  }

  queryFeathersPromise(query: {}): Promise<Assistance[]> {
    return this.feathers.getPromise(Assistance, query)
  }

  async create(assistance: Assistance, propsToCreateFrom?: any): Promise<Assistance> {
    console.log(this.TAG, 'create assistance:', assistance, 'propsToCreateFrom', propsToCreateFrom)
    if (propsToCreateFrom) {
      assistance.updateFromProps(propsToCreateFrom)
    }
    return this.feathers.createWithFactory(Assistance, assistance)
  }

  updateNonRecurrently(assistance: Assistance): Promise<Assistance> {
    console.log(this.TAG, 'updateNonRecurrently', assistance)
    return this.feathers.upsertWithFactory(Assistance, assistance)
  }

  patchExperimental(propsWithId: PartialWithId<Assistance>): Promise<Assistance> {
    console.log(this.TAG, 'patchNonRecurrently', propsWithId)
    return this.feathers.patchWithFactory(Assistance, propsWithId)
  }

  patchNonRecurrently(propsWithId: PartialWithId<Assistance>): Promise<Assistance> {
    console.log(this.TAG, 'patchNonRecurrently', propsWithId)
    return this.feathers.patchWithFactory(Assistance, propsWithId)
  }

  patchNonRecurrentlyMulti(ids: number[], props: Partial<Assistance>): Promise<Assistance | Assistance[]> {
    return this.feathers.patchMultiByIds(Assistance, ids, props)
  }

  patchAssignment(assignment: Partial<AssistantAssignment>): Promise<AssistantAssignment> {
    return this.feathers.patchWithFactory(AssistantAssignment, assignment)
  }

  isOverlappingAnyAssistanceForTheSameClient(
    plannedFrom: Date,
    plannedTo: Date,
    clientId: number,
    currentAssistanceId: number,
    assistanceBeforeValueChange: Pick<
      Assistance,
      'getDateFrom' | 'getDateTo' | 'recurrenceParentId' | 'bundleParentId'
    >,
    rrule: string,
  ): Observable<DateRange[]> {
    return this.feathers
      .findOnGenericServiceWithArrayRecordStore('assistance-overlaps', {
        from: plannedFrom,
        to: plannedTo,
        clientId,
        oldAssistanceDateFrom: assistanceBeforeValueChange.getDateFrom(),
        oldAssistanceDateTo: assistanceBeforeValueChange.getDateTo(),
        currentAssistanceId,
        oldAssistanceRecurrenceParentId: assistanceBeforeValueChange.recurrenceParentId,
        bundleParentId: assistanceBeforeValueChange.bundleParentId,
        rrule,
      })
      .pipe(
        map((results: { from: string; to: string }[]) =>
          results.map((res) => ({
            from: new Date(res.from),
            to: new Date(res.to),
          })),
        ),
      )
  }

  isOverlappingAnyAssistanceForTheSameAssistant(
    plannedFrom: Date,
    plannedTo: Date,
    assistantIds: number[],
    currentAssistanceId: number,
  ): Observable<DateRange[]> {
    return this.feathers
      .findOnGenericServiceWithArrayRecordStore('assistance-assistant-overlaps', {
        from: plannedFrom,
        to: plannedTo,
        assistantIds,
        currentAssistanceId,
      })
      .pipe(
        map((results: { from: string; to: string }[]) =>
          results.map((res) => ({
            from: new Date(res.from),
            to: new Date(res.to),
          })),
        ),
      )
  }

  async remove(assistance: Assistance) {
    console.log(this.TAG, 'remove', assistance)
    if (Assistance.isRecurrentButNotException(assistance)) {
      const newSeries = await this.update(assistance, RecurrenceEditingMode.currentOccurrenceAndFollowing)
      return await this.feathers.remove(Assistance.serviceName, newSeries)
    } else {
      return await this.feathers.remove(Assistance.serviceName, assistance)
    }
  }

  async updateAvailabilitiesOnAssistance(assistance: Assistance, availabilities: AssistantAvailability[]) {
    this.assistanceLocker.lock(assistance)
    const { assignments, skippedAssignments } = await this.assignmentService.setAssignments(
      assistance.URID,
      availabilities,
    )
    this.assistanceLocker.markForUnlock(assistance)

    console.log(this.TAG, 'updateAvailabilitiesOnAssistance end')

    this.notifySkippedAssignmentsIfNeeded(skippedAssignments)

    return assignments
  }

  async removeAvailabilitiesFromAssistance(assistance: Assistance): Promise<RemovalResult> {
    this.assistanceLocker.lock(assistance)
    const removalResult = await this.assignmentService.removeAllAssignmentsFromAssistance(assistance.id)
    this.assistanceLocker.markForUnlock(assistance)
    this.notifySkippedAssignmentsIfNeeded(removalResult.skippedAssignments)
    return removalResult
  }

  private notifySkippedAssignmentsIfNeeded(skippedAssignments: SkippedAssignment[]): void {
    if (!skippedAssignments.length) {
      return
    }
    const names = this.formatSkippedAssistantNames(skippedAssignments)
    this.primeNgToastService.displayCustomToast('warn', {
      key: 'EVENT_EDITOR.ASSIGNMENTS_SKIPPED_CARE_LOGS',
      params: {
        names,
      },
    })
  }

  private formatSkippedAssistantNames(skippedAssignments: SkippedAssignment[]): string {
    return skippedAssignments
      .map((s) => {
        const user = s.assignment?.assistant?.worker?.user
        if (user && (user.firstName != null || user.lastName != null)) {
          return [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
        }
        return `ID ${s.assignment.assistantId}`
      })
      .join(', ')
  }

  getCountOfNextWeekAssistancesWithChanges() {
    return this.feathers
      .findOnGenericServiceWithOneRecordStore<AssistanceChangesCount>('assistance-changes-field-observer')
      .pipe(map((changes) => changes.count))
  }

  createNewAssistance(props?: Partial<Assistance>) {
    return Assistance.createFromProps(Assistance, props)
  }

  getById(assistanceId: number) {
    return this.queryFeathers({ id: assistanceId }).pipe(
      map((a) => a[0]),
      filter((a) => a.id === assistanceId),
    )
  }

  getByIds(assistanceIds: number[], queryType: AssistanceQueryType = 'full') {
    return this.queryFeathers({
      queryType,
      id: {
        $in: assistanceIds,
      },
    })
  }

  getBundleChildAssistances(parentAssistance: Assistance) {
    console.log('getBundleChildAssistances', parentAssistance.id)
    return this.queryFeathers({
      bundleParentId: parentAssistance.id,
    }).pipe(map((aa) => aa.filter((a) => a.bundleParentId === parentAssistance.id)))
  }

  assistanceCanBeStartedByAssistant(assistance: Assistance, assistantId: number) {
    if (assistance.nonClientWork) {
      return false
    }

    const assignment = assistance.assistantAssignments.getAssignmentByAssistantId(assistantId)
    const assignmentStatus = assignment.status
    const assistanceStatus = assistance.status

    // TODO asistent nesmi mit bezici jinou asistenci (ma realizedFrom ale nema realizedTo) - asi by mel byt nejaky field na asistentovi "currentlyRunningAssistanceId" kam by se zapisovalo pokud by asistent (nikoli koordinator) tady tim fabem pustil asistenci. A mazalo pokud by ji uzavrel.
    return (
      !assistance.realizedFrom &&
      !assistance.realizedTo &&
      assistanceStatus.name === AssistanceStatusName.CONFIRMED &&
      assignmentStatus.name === AssistanceStatusName.CONFIRMED &&
      DateUtils.isInMinuteWindow(new Date(), assistance.getDateFrom(), 240)
    )
  }

  public async deleteWithConfirmation(assistance: Assistance, successCallback?: () => void) {
    const alert = await this.alertCtrl.create({
      header: 'Opravdu smazat?',
      message: 'Opravdu si přejete smazat tuto asistenci?',
      buttons: [
        {
          text: 'Zrušit',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: 'Smazat',
          cssClass: 'primary',
          handler: async () => {
            // TODO - this could be optimized (later) - currently we save the assistance before deleting to create exception on parent, and only after that we delete the exception instance. We could do it just by appending exception to the parent and save a roundtrip to server
            if (Assistance.isRecurrent(assistance)) {
              await this.update(assistance, RecurrenceEditingMode.currentOccurrence)
            }

            await this.remove(assistance)

            if (successCallback) {
              successCallback()
            }
          },
        },
      ],
    })

    await alert.present()
  }

  getByURID(URID: string): Observable<Assistance> {
    const parsedURID = RecurrenceUtil.parseURID(URID)
    if (!parsedURID) {
      throw new Error(`Invalid URID format: ${URID}`)
    }

    if (parsedURID.id !== undefined) {
      return this.queryFeathers({
        id: parsedURID.id,
      }).pipe(
        take(1),
        map((a) => a[0]),
      )
    } else {
      return this.queryFeathers({
        id: parsedURID.recurrenceParentId,
      }).pipe(
        take(1),
        map((a) => a[0]),
        tap((e) => console.log('parsed from URID', e)),
        map((a) => a.generateOccurrence(parsedURID.plannedFrom)),
        tap((e) => console.log('parsed from URID with generated times', e)),
      )
    }
  }

  async patch(URID: string, patchData: Partial<Assistance>) {
    await this.feathers.patchByUrid(Assistance, { urid: URID, ...patchData })
  }
}
